import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import { navigatedToNextStep } from "../../../../redux/slice/bookingCreation/bookingCreationSlice";
import { calculateDisplayPriceForReservations } from "../../../../util/payments";
import { Button } from "components/ui/button";
import { cn } from "../../../../util/cn";
import { ArrowRight } from "lucide-react";

interface BookLocationNavigateNextActionProps {
  // When this value changes, the button will subtly pulse to draw attention
  bounceKey?: string | number | null;
}

const BookLocationNavigateNextAction: React.FC<
  BookLocationNavigateNextActionProps
> = ({ bounceKey }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const nextEnabled = useSelector(
    (state: RootState) => state.bookingCreation.nextEnabled
  );
  const step = useSelector((state: RootState) => state.bookingCreation.step);
  const useCase = useSelector(
    (state: RootState) => state.bookingCreation.useCase
  );

  const isZeroAmountBooking = useSelector(
    (state: RootState) =>
      calculateDisplayPriceForReservations(
        state.bookingCreation.reservations
      ) === 0
  );

  let showBookButton;
  let showProceedToPaymentButton;

  if (useCase === "rental" || useCase === "p2p" || useCase === "sup") {
    const stepMapping = {
      rental: { bookStep: 3, paymentStep: 4 },
      sup: { bookStep: 3, paymentStep: 4 },
      p2p: { bookStep: 4, paymentStep: 5 },
    };

    // @ts-ignore
    const isBookStep = step === stepMapping[useCase]?.bookStep;
    // @ts-ignore
    const isPaymentStep = step === stepMapping[useCase]?.paymentStep;

    showBookButton = isBookStep || (isPaymentStep && isZeroAmountBooking);
    showProceedToPaymentButton = isPaymentStep && !isZeroAmountBooking;
  } else if (useCase === "voucher") {
    // Step 1 = bookingSummary → show "Zur Kasse" (proceed to payment)
    showProceedToPaymentButton = step === 1;
  }

  const handleNextClick = () => {
    dispatch(navigatedToNextStep());
  };

  // Smooth, subtle pulse animation (scale) when a key action happens, e.g., location selected
  const [pulsing, setPulsing] = React.useState(false);
  React.useEffect(() => {
    if (bounceKey == null) return;
    // Only pulse if the action is actually actionable (button enabled)
    if (!nextEnabled) return;
    setPulsing(true);
    const t = setTimeout(() => setPulsing(false), 250);
    return () => clearTimeout(t);
  }, [bounceKey, nextEnabled]);

  return (
    <Button
      size="lg"
      variant="primary"
      className={cn(
        "w-full transition-all duration-300 ease-out will-change-transform",
        pulsing && "scale-[1.03]"
      )}
      disabled={!nextEnabled}
      onClick={handleNextClick}
    >
      {t(
        !showBookButton && !showProceedToPaymentButton
          ? "book_location.next"
          : showBookButton
          ? "book_location.confirmation.order_now"
          : "book_location.confirmation.proceed_to_payment"
      )}
      {(showProceedToPaymentButton ||
        (useCase === "voucher" && !showBookButton)) && (
        <ArrowRight className="ml-2 h-5 w-5" />
      )}
    </Button>
  );
};

export default BookLocationNavigateNextAction;
